import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraInput, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { proposalReviewClient } from '../../../../lib/connect/clients';
import { DocumentMaterial, isDocumentMaterial, materialVersionText } from './DocumentMaterial';
import { OUTCOME_OPTIONS, outcomeLabel, SEVERITIES } from './reviewState';
import { ReviewStateLabel } from './SectionReviewStatus';
import { reviewErrorMessage, reviewKeys, useAllSectionReviewStates, useReviewDetail, useReviewSections } from './useProposalReviews';
const fmt = (d)=>d ? d.toDate().toLocaleDateString() : '';
/** Runs one mutation at a time: a second call while one is in flight is dropped. */ function useAction(onError, refresh) {
    const lock = useRef(false);
    const [busy, setBusy] = useState(false);
    return {
        busy,
        run: async (fn)=>{
            if (lock.current) return false;
            lock.current = true;
            setBusy(true);
            onError('');
            try {
                await fn();
                await refresh();
                return true;
            } catch (err) {
                onError(reviewErrorMessage(err));
                return false;
            } finally{
                lock.current = false;
                setBusy(false);
            }
        }
    };
}
export function ClosedBanner({ review }) {
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.proposalReviews.closedBanner,
        className: "rounded-lg border border-border bg-muted p-3 text-sm text-foreground",
        children: [
            "Closed ",
            fmt(review.closedAt),
            " — ",
            outcomeLabel(review.outcome),
            ". This review and its findings are history for the versions listed.",
            review.closureSummary && /*#__PURE__*/ _jsx("p", {
                className: "mt-1 text-muted-foreground",
                children: review.closureSummary
            })
        ]
    });
}
export function ProposalReviewDetailPage() {
    const { id = '', reviewId = '' } = useParams();
    const queryClient = useQueryClient();
    const detail = useReviewDetail(id, reviewId);
    const { sections } = useReviewSections(id);
    const review = detail.data?.review;
    const materials = detail.data?.materials ?? [];
    const findings = detail.data?.findings ?? [];
    const sectionIds = sections.map((s)=>s.sectionId);
    const states = useAllSectionReviewStates(id, sectionIds).data;
    const sectionById = new Map(sections.map((s)=>[
            s.sectionId,
            s
        ]));
    const [error, setError] = useState('');
    const refresh = async ()=>{
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: reviewKeys.all(id)
            }),
            queryClient.invalidateQueries({
                queryKey: reviewKeys.states(id)
            })
        ]);
    };
    const pin = useAction(setError, refresh);
    const finding = useAction(setError, refresh);
    const close = useAction(setError, refresh);
    const [severity, setSeverity] = useState('note');
    const [body, setBody] = useState('');
    const [materialId, setMaterialId] = useState('');
    const [outcome, setOutcome] = useState('');
    const [summary, setSummary] = useState('');
    if (detail.isError) {
        return /*#__PURE__*/ _jsx("p", {
            role: "alert",
            "data-testid": Testids.proposalReviews.error,
            className: "p-4 text-sm text-danger",
            children: reviewErrorMessage(detail.error)
        });
    }
    if (!review) return /*#__PURE__*/ _jsx("p", {
        className: "p-4 text-sm text-muted-foreground",
        children: "Loading…"
    });
    const open = review.status === 'open';
    const scopeIds = review.scopeSectionIds.length ? review.scopeSectionIds : sectionIds;
    const nameOf = (sectionId)=>sectionById.get(sectionId)?.title || 'Untitled section';
    const sectionLink = (sectionId)=>{
        const s = sectionById.get(sectionId);
        return s ? `/proposal/${id}/v2/volume/${s.volumeId}/section/${sectionId}` : '';
    };
    const pinBlocked = scopeIds.length === 0 ? 'There are no sections to pin.' : '';
    return /*#__PURE__*/ _jsxs("div", {
        className: "mx-auto flex w-full max-w-4xl flex-col gap-5 px-4 pt-4 sm:px-6",
        children: [
            /*#__PURE__*/ _jsxs("header", {
                children: [
                    /*#__PURE__*/ _jsx(Link, {
                        to: `/proposal/${id}/v2/reviews`,
                        className: "text-sm text-muted-foreground hover:underline",
                        children: "← Reviews"
                    }),
                    /*#__PURE__*/ _jsx("h1", {
                        className: "text-lg font-semibold text-foreground",
                        children: review.name
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm text-muted-foreground",
                        children: [
                            "Starts ",
                            review.startsOn,
                            review.dueOn ? ` · Due ${review.dueOn}` : '',
                            review.reviewers.length ? ` · Reviewers: ${review.reviewers.join(', ')}` : ''
                        ]
                    })
                ]
            }),
            !open && /*#__PURE__*/ _jsx(ClosedBanner, {
                review: review
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.proposalReviews.error,
                className: "text-sm text-danger",
                children: error
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "font-medium text-foreground",
                        children: "Scope"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1 text-sm",
                        children: scopeIds.map((sid)=>/*#__PURE__*/ _jsxs("li", {
                                className: "flex items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        children: nameOf(sid)
                                    }),
                                    /*#__PURE__*/ _jsx(ReviewStateLabel, {
                                        state: states?.get(sid)
                                    })
                                ]
                            }, sid))
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "font-medium text-foreground",
                                children: "Pinned materials"
                            }),
                            open && /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.proposalReviews.pinButton,
                                disabled: !!pinBlocked || pin.busy,
                                onClick: ()=>pin.run(()=>proposalReviewClient.pinReviewMaterials({
                                            projectId: id,
                                            reviewId,
                                            sectionIds: scopeIds
                                        })),
                                children: pin.busy ? 'Pinning…' : 'Pin current versions'
                            })
                        ]
                    }),
                    open && pinBlocked && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: pinBlocked
                    }),
                    materials.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No versions pinned yet."
                    }),
                    materials.map((m)=>isDocumentMaterial(m) ? /*#__PURE__*/ _jsx(DocumentMaterial, {
                            material: m,
                            sectionHref: sectionLink(m.sectionId),
                            title: nameOf(m.sectionId)
                        }, m.id) : /*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.proposalReviews.materialRow,
                            className: "rounded-lg border border-border bg-card p-3 text-sm",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: nameOf(m.sectionId)
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraBadge, {
                                            variant: "violet",
                                            children: [
                                                "v",
                                                m.sectionVersion,
                                                " pinned"
                                            ]
                                        }),
                                        sectionLink(m.sectionId) && /*#__PURE__*/ _jsx(Link, {
                                            to: sectionLink(m.sectionId),
                                            className: "text-brand hover:underline",
                                            children: "Open section and version history"
                                        })
                                    ]
                                }),
                                m.snapshotComment && /*#__PURE__*/ _jsx("p", {
                                    className: "mt-1 text-muted-foreground",
                                    children: m.snapshotComment
                                })
                            ]
                        }, m.id))
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "font-medium text-foreground",
                        children: "Findings"
                    }),
                    findings.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No findings yet."
                    }),
                    findings.map((f)=>{
                        const m = materials.find((x)=>x.id === f.materialId);
                        return /*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.proposalReviews.findingRow,
                            className: "rounded-lg border border-border bg-card p-3 text-sm",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: f.severity === 'critical' || f.severity === 'major' ? 'danger' : 'neutral',
                                            children: f.severity || 'note'
                                        }),
                                        m && /*#__PURE__*/ _jsxs("span", {
                                            className: "text-muted-foreground",
                                            children: [
                                                nameOf(m.sectionId),
                                                " ",
                                                materialVersionText(m)
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-1 whitespace-pre-wrap",
                                    children: f.body
                                })
                            ]
                        }, f.id);
                    }),
                    open && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2 rounded-lg border border-border p-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraSelect, {
                                        "aria-label": "Severity",
                                        "data-testid": Testids.proposalReviews.findingSeverity,
                                        value: severity,
                                        onChange: (e)=>setSeverity(e.target.value),
                                        children: SEVERITIES.map((s)=>/*#__PURE__*/ _jsx("option", {
                                                value: s,
                                                children: s
                                            }, s))
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                                        "aria-label": "Material",
                                        "data-testid": Testids.proposalReviews.findingMaterial,
                                        value: materialId,
                                        onChange: (e)=>setMaterialId(e.target.value),
                                        children: [
                                            /*#__PURE__*/ _jsx("option", {
                                                value: "",
                                                children: "Whole review"
                                            }),
                                            materials.map((m)=>/*#__PURE__*/ _jsxs("option", {
                                                    value: m.id,
                                                    children: [
                                                        nameOf(m.sectionId),
                                                        " ",
                                                        materialVersionText(m)
                                                    ]
                                                }, m.id))
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                "aria-label": "Finding",
                                "data-testid": Testids.proposalReviews.findingBody,
                                placeholder: "What did you find?",
                                rows: 3,
                                value: body,
                                onChange: (e)=>setBody(e.target.value)
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.proposalReviews.addFinding,
                                disabled: !body.trim() || finding.busy,
                                onClick: async ()=>{
                                    const ok = await finding.run(()=>proposalReviewClient.addReviewFinding({
                                            projectId: id,
                                            reviewId,
                                            materialId,
                                            severity,
                                            body: body.trim()
                                        }));
                                    if (ok) setBody('');
                                },
                                children: finding.busy ? 'Adding…' : 'Add finding'
                            })
                        ]
                    })
                ]
            }),
            open && /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2 rounded-lg border border-border p-3",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "font-medium text-foreground",
                        children: "Close review"
                    }),
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Outcome",
                        "data-testid": Testids.proposalReviews.closeOutcome,
                        value: outcome,
                        onChange: (e)=>setOutcome(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Choose an outcome…"
                            }),
                            OUTCOME_OPTIONS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o,
                                    children: outcomeLabel(o)
                                }, o))
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": "Summary",
                        "data-testid": Testids.proposalReviews.closeSummary,
                        placeholder: "Summary (optional)",
                        value: summary,
                        onChange: (e)=>setSummary(e.target.value)
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.proposalReviews.closeButton,
                        disabled: !outcome || close.busy,
                        onClick: ()=>close.run(()=>proposalReviewClient.closeProposalReview({
                                    projectId: id,
                                    reviewId,
                                    outcome,
                                    closureSummary: summary.trim()
                                })),
                        children: close.busy ? 'Closing…' : 'Close review'
                    }),
                    !outcome && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "Choose an outcome to close."
                    })
                ]
            })
        ]
    });
}
